import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getAmenityV2,
  getBuildingAmenitiesV2,
  removeAmenity,
  setFilteredAmenities
} from '../../../../../redux/actions/amenities';
import { CPagination } from '@coreui/react';
import DeleteModal from '../../../../../components/DeleteModal';
import { changeOpenedModal } from '../../../../../redux/actions/headerModal';
import { useHistory } from 'react-router-dom';
import { setToast } from '../../../../../redux/actions/window';
import { bookingFromAmenity } from '../../../../../redux/actions/bookingParAm';
import { useTranslation } from 'react-i18next';
import { requestPending } from '../../../../../utils/status';
import { GET_BUILDING_AMENITIES_V2 } from '../../../../../redux/constants';
import { setCsvFields } from '../../../../../redux/actions/reports';
import AmenitiesListView from './AmenitiesListView';
import { resolveViewingBuildingTimezone } from './amenitiesListDisplayHelpers';

const itemsPerPage = 20;

const Amenities = ({ buildingId: buildingIdProp } = {}) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const { building, globalBuildingId } = useSelector((state) => state.building);
  const { amenities, filteredAmenities, status } = useSelector((state) => state.amenities);
  const [showModal, setShowModal] = useState({
    id: null,
    show: false
  });
  const history = useHistory();
  const [activePage, setActivePage] = useState(1);
  const [numOfPages, setNumOfPages] = useState(1);

  const resolvedBuildingId = useMemo(() => {
    const fromUrl = history.location.pathname.split('/')[2];
    const raw = buildingIdProp ?? fromUrl ?? globalBuildingId;
    if (raw === null || raw === undefined || raw === '') return null;
    const n = typeof raw === 'number' ? raw : parseInt(raw, 10);
    return Number.isNaN(n) ? null : n;
  }, [buildingIdProp, history.location.pathname, globalBuildingId]);

  const statusTimezone = useMemo(() => {
    if (Number(building?.id) !== Number(resolvedBuildingId)) return null;
    return resolveViewingBuildingTimezone(building);
  }, [building, resolvedBuildingId]);

  useEffect(() => {
    if (amenities.length > 0) setNumOfPages(Math.ceil(amenities.length / itemsPerPage));
  }, [amenities]);

  useEffect(() => {
    if (amenities.length > 0) {
      const lastItemIndx = activePage * itemsPerPage;
      const firstItemIndx = lastItemIndx - itemsPerPage;

      dispatch(setFilteredAmenities(amenities.slice(firstItemIndx, lastItemIndx)));
    } else {
      dispatch(setFilteredAmenities([]));
    }
  }, [numOfPages, activePage, amenities, dispatch]);

  useEffect(() => {
    dispatch(
      setCsvFields([
        {
          key: 'description',
          label: t('Amenities')
        },
        {
          key: 'hours_display',
          label: t('Hours')
        },
        {
          key: 'booking_type_display',
          label: t('Booking Type')
        },
        {
          key: 'manager_approval_display',
          label: t('Manager Approval')
        },
        {
          key: 'fee_display',
          label: t('Fee')
        }
      ])
    );
  }, [dispatch, t]);

  useEffect(() => {
    if (resolvedBuildingId == null || resolvedBuildingId <= 0) return;
    dispatch(getBuildingAmenitiesV2({ id: resolvedBuildingId }));
  }, [dispatch, resolvedBuildingId]);

  const handleEdit = (id) => {
    dispatch(getAmenityV2({ id }));
    dispatch(changeOpenedModal({ modal: 'updateAmenityV2', data: { amenityId: id } }));
  };

  const handleDetails = (item) => {
    dispatch(getAmenityV2({ id: item.id }));
    dispatch(
      changeOpenedModal({ modal: 'amenityDetailV2', data: { amenityId: item.id } })
    );
  };

  const handleDelete = () => {
    dispatch(
      removeAmenity({
        id: showModal.id,
        success: () => {
          dispatch(getBuildingAmenitiesV2({ id: building?.id ?? resolvedBuildingId }));
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: 'Amenity deleted successfully!'
            })
          );
          setShowModal((prev) => ({
            ...prev,
            show: false
          }));
        },
        fail: () => {
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: 'Amenity deletion failed!',
              modal: 'errorToast'
            })
          );
        }
      })
    );
  };

  const handleBooking = (item) => {
    dispatch(
      changeOpenedModal({
        modal: 'addBookingV2',
        data: {
          preselectedAmenity: {
            id: item.id,
            label: item.description,
            type_id: item.type_id
          },
          preselectedBuilding: {
            id: resolvedBuildingId,
            label: building?.name ?? ''
          }
        }
      })
    );
  };

  const loading = status === requestPending(GET_BUILDING_AMENITIES_V2);

  return (
    <>
      <AmenitiesListView
        items={filteredAmenities}
        loading={loading}
        statusTimezone={statusTimezone}
        onRowClick={handleDetails}
        onEdit={handleEdit}
        onDetail={handleDetails}
        onBooking={handleBooking}
        setShowModal={setShowModal}
      />
      <div className="mt-2">
        <CPagination
          activePage={activePage}
          pages={numOfPages}
          onActivePageChange={(i) => setActivePage(i)}
        />
      </div>

      {showModal.show && (
        <DeleteModal
          type="Amenity"
          action={handleDelete}
          setShowModal={setShowModal}
          description={'Are you sure you want to delete this amenity?'}
        />
      )}

    </>
  );
};

export default Amenities;
